import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { PaymentMethod, usePermissions } from '@tyro/api';
import { RHFRadioGroup, RHFTextField, useFormValidator } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { RHFContactAutocomplete } from '@tyro/people';
import get from 'lodash/get';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type { ReturnTypeFromUseStudentFees } from '../../../api/student-fees';
import { PaymentMethodSelect } from './fields/payment-method';
import {
  PaymentDetailsForPartialPayment,
  PaymentDetailsForPayInFull,
  PaymentDetailsForPaymentPlan,
} from './payment-details';
import { usePayFeesSettings } from './store';
import type { PayFeesFormValues } from './types';

interface PayFeesStepOneProps {
  feesToPay: ReturnTypeFromUseStudentFees[];
}

export function PayFeesStepOne({ feesToPay }: PayFeesStepOneProps) {
  const { t } = useTranslation(['common', 'fees']);
  const { isStaffUser } = usePermissions();
  const {
    setNextAction,
    paymentsToPayAndMethod,
    setPaymentsToPayAndMethod,
    nextStep,
  } = usePayFeesSettings();

  const { resolver, rules } = useFormValidator<PayFeesFormValues>();
  const { handleSubmit, control, watch, setValue } = useForm<PayFeesFormValues>(
    {
      resolver: resolver({
        paymentMethod: [rules.required()],
        onBehalfOf: rules.required(),
        feesById: {
          amountToPay: [
            rules.required(),
            rules.isNumber(),
            rules.min(0.5),
            rules.validate<string>(
              (value, throwError, formValues, fieldName) => {
                const pathToCurrentFee = fieldName.substring(
                  0,
                  fieldName.lastIndexOf('.'),
                );
                const fee = get(
                  formValues,
                  pathToCurrentFee,
                ) as PayFeesFormValues['feesById'][number];
                const valueAsNumber = Number(value);
                if ((valueAsNumber ?? 0) > fee.amount - fee.amountPaid) {
                  throwError(t('fees:amountToPayExceedsTheAmountThatsDue'));
                }
              },
            ),
          ],
        },
      }),
      defaultValues: {
        paymentMethod:
          paymentsToPayAndMethod?.paymentMethod ?? PaymentMethod.Card,
        paymentType: 'full',
        feesById: feesToPay
          .map((fee) => ({
            ...fee,
            amountToPay: fee.amountDue ?? 0,
          }))
          .reduce(
            (acc, fee) => {
              acc[`${fee.id.studentPartyId}${fee.id.feeId}`] = fee;
              return acc;
            },
            {} as PayFeesFormValues['feesById'],
          ),
      },
    },
  );

  const [paymentMethod, paymentType, feesById] = watch([
    'paymentMethod',
    'paymentType',
    'feesById',
  ]);

  const studentIds = useMemo(
    () => feesToPay.map((fee) => fee.person.partyId),
    [feesToPay],
  );

  const onSubmit = handleSubmit((values) => {
    const feesList = Object.values(values.feesById);

    const filteredFees =
      values.paymentType === 'paymentPlan'
        ? feesList.filter(({ paymentPlan }) => paymentPlan)
        : feesList;

    setPaymentsToPayAndMethod({
      total: feesList.reduce((acc, fee) => acc + Number(fee.amountToPay), 0),
      paymentMethod: values.paymentMethod,
      fees: filteredFees.map((fee) => ({
        ...fee,
        amountToPay: Number(fee.amountToPay),
      })),
      onBehalfOf: values?.onBehalfOf,
      details: values.details,
      cardPaymentType:
        values.paymentType === 'paymentPlan' ? 'payment-plan' : 'one-time',
    });
    nextStep();
  });

  useEffect(() => {
    const updatedFeesById = {} as typeof feesById;

    for (const [key, fee] of Object.entries(feesById)) {
      let amountToPay = 0;
      switch (paymentType) {
        case 'full': {
          amountToPay = fee.amountDue ?? 0;
          break;
        }
        case 'partial': {
          amountToPay = 0;
          break;
        }
        case 'paymentPlan': {
          amountToPay = fee?.paymentPlanInstallments?.[0]?.amount ?? 0;
          break;
        }
      }

      updatedFeesById[key] = { ...fee, amountToPay };
    }

    setValue('feesById', updatedFeesById);
  }, [paymentType]);

  useEffect(() => {
    setNextAction(() => onSubmit);
  }, []);

  useEffect(() => {
    if (paymentType === 'paymentPlan') {
      setValue('paymentType', 'full');
    }
  }, [paymentMethod]);

  const fields = Object.values(feesById);

  const someAllowPartialPayment = fields.some(
    (field) => field.allowPartialPayments,
  );
  const someAllowPaymentPlan = fields.some((field) => field.paymentPlan);

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={3}>
        {isStaffUser && (
          <>
            <PaymentMethodSelect
              controlProps={{
                name: 'paymentMethod',
                control,
              }}
            />
            <RHFContactAutocomplete
              controlProps={{
                name: 'onBehalfOf',
                control,
              }}
              label={t('fees:onBehalfOf')}
              contactsFilter={{
                studentPartyIds: studentIds,
              }}
            />
            <RHFTextField
              controlProps={{
                name: 'details',
                control,
              }}
              label={t('common:details')}
              textFieldProps={{
                multiline: true,
                minRows: 2,
              }}
            />
          </>
        )}
        <RHFRadioGroup
          controlProps={{
            name: 'paymentType',
            control,
          }}
          radioGroupProps={{
            row: true,
          }}
          label={
            <Stack direction="row" gap={0.5} alignItems="center">
              {t('fees:paymentType')}
            </Stack>
          }
          renderOption={(option, renderRadio) => {
            if (option.value === 'paymentPlan' && !someAllowPaymentPlan) {
              return (
                <Tooltip
                  key={option.value}
                  title={t('fees:paymentPlansNotAllowed', {
                    count: fields.length,
                  })}
                  placement="top"
                >
                  <span>{renderRadio({ disabled: true })}</span>
                </Tooltip>
              );
            }

            if (option.value === 'partial' && !someAllowPartialPayment) {
              return (
                <Tooltip
                  title={t('fees:partialPaymentsNotAllowed', {
                    count: fields.length,
                  })}
                  placement="top"
                >
                  <span>{renderRadio({ disabled: true })}</span>
                </Tooltip>
              );
            }

            return renderRadio({});
          }}
          options={
            [
              {
                label: t('fees:payInFull'),
                value: 'full',
              },
              {
                label: t('fees:partialPayment'),
                value: 'partial',
              },
              ...(paymentMethod === PaymentMethod.Card
                ? [
                    {
                      label: t('fees:paymentPlan'),
                      value: 'paymentPlan',
                    },
                  ]
                : []),
            ] as const
          }
        />
        {paymentType === 'full' && (
          <PaymentDetailsForPayInFull control={control} />
        )}
        {paymentType === 'partial' && (
          <PaymentDetailsForPartialPayment control={control} />
        )}
        {paymentType === 'paymentPlan' && (
          <PaymentDetailsForPaymentPlan control={control} />
        )}
      </Stack>
    </form>
  );
}
